import React, { useEffect } from 'react';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { ImageKitProvider } from '@imagekit/react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import AOS from 'aos';
import Navbar from './components/Navbar';
import HeroCarousel from './components/HeroCarousel';
import AboutSection from './components/AboutSection';
import CausesSection from './components/CausesSection';
import ServicesSection from './components/ServicesSection';
import DonateSection from './components/DonateSection';
import TeamSection from './components/TeamSection';
import TestimonialSection from './components/TestimonialSection';
import Footer from './components/Footer';
import AboutPage from './components/AboutPage';
import CausesPage from './components/CausesPage';
import EducationCausePage from './components/EducationCausePage';
import TreePlantationCausePage from './components/TreePlantationCausePage';
import HealthyLifeCausePage from './components/HealthyLifeCausePage';
import TestimonialPage from './components/TestimonialPage';
import ContactPage from './components/ContactPage';
import EventsPage from './components/EventsPage';
import NotFoundPage from './components/NotFoundPage';
import RefundPolicyPage from './components/RefundPolicyPage';
import TermsPage from './components/TermsPage';
import ContactInfoPage from './components/ContactInfoPage';
import AdminApp from './admin/AdminApp';
import './App.css';
import { queryClient, queryPersistOptions } from './queryClient';

const HomePage: React.FC = () => {
  useEffect(() => {
    // Initialize AOS (Animate On Scroll)
    AOS.init({
      duration: 1000,
      easing: 'ease-in-out',
      once: true,
      mirror: false,
    });

    // Hide spinner after page loads
    const spinner = document.getElementById('spinner');
    if (spinner) {
      setTimeout(() => {
        spinner.classList.remove('show');
      }, 500);
    }

    // Back to top button functionality
    const backToTop = document.querySelector('.back-to-top');
    if (backToTop) {
      const handleScroll = () => {
        if (window.scrollY > 300) {
          (backToTop as HTMLElement).style.display = 'block';
        } else {
          (backToTop as HTMLElement).style.display = 'none';
        }
      };

      window.addEventListener('scroll', handleScroll);

      backToTop.addEventListener('click', (e) => {
        e.preventDefault();
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });

      return () => {
        window.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  return (
    <>
      <div
        id="spinner"
        className="show bg-white position-fixed translate-middle w-100 vh-100 top-50 start-50 d-flex align-items-center justify-content-center"
      >
        <div className="spinner-grow text-primary" role="status"></div>
      </div>

      <Navbar />
      <HeroCarousel />
      <AboutSection />
      <CausesSection />
      <ServicesSection />
      <DonateSection />
      <TeamSection />
      <TestimonialSection />
      <Footer />

      <button type="button" className="btn btn-lg btn-primary btn-lg-square back-to-top" aria-label="Back to top">
        <i className="bi bi-arrow-up"></i>
      </button>
    </>
  );
};

const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [pathname]);

  return null;
};

const imageKitUrl = process.env.REACT_APP_IMAGEKIT_URL || 'https://ik.imagekit.io/thehumanitytrust';

const App: React.FC = () => {
  return (
    <PersistQueryClientProvider client={queryClient} persistOptions={queryPersistOptions}>
      <ImageKitProvider urlEndpoint={imageKitUrl}>
      <BrowserRouter>
        <ScrollToTop />
        <div className="App">
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/causes" element={<CausesPage />} />
            <Route path="/causes/education-indian-children" element={<EducationCausePage />} />
            <Route path="/causes/tree-plantation" element={<TreePlantationCausePage />} />
            <Route path="/causes/healthy-life" element={<HealthyLifeCausePage />} />
            <Route path="/testimonial" element={<TestimonialPage />} />
            <Route path="/events" element={<EventsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/refund-policy" element={<RefundPolicyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/contact-info" element={<ContactInfoPage />} />
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </div>
      </BrowserRouter>
      </ImageKitProvider>
    </PersistQueryClientProvider>
  );
};

export default App;
